import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { styled, keyframes } from "@mui/system";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

// Keyframes for animations
const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 0.8;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0) translateX(0);
    opacity: 0.4;
  }
  25% {
    transform: translateY(-5px) translateX(2px);
    opacity: 0.6;
  }
  50% {
    transform: translateY(-10px) translateX(5px);
    opacity: 0.8;
  }
  75% {
    transform: translateY(-5px) translateX(2px);
    opacity: 0.6;
  }
  100% {
    transform: translateY(0) translateX(0);
    opacity: 0.4;
  }
`;

const dotPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.7;
  }
`;

const cellPulse = keyframes`
  0%, 100% {
    box-shadow: inset 0 -1px 0 rgba(16, 185, 129, 0.03), inset -1px 0 0 rgba(16, 185, 129, 0.03);
  }
  50% {
    box-shadow: inset 0 -1px 0 rgba(16, 185, 129, 0.1), inset -1px 0 0 rgba(16, 185, 129, 0.1);
  }
`;

// Styled components
const CardContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "448px",
  margin: "0 auto",
  cursor: "pointer",
}));

const CardTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  fontWeight: 500,
  marginBottom: "0.5rem",
}));

const MatrixCard = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "286px",
  borderRadius: "0.8rem",
  backgroundColor: "#065f46", // emerald-950 equivalent
  //boxShadow: theme.shadows[10],
  position: "relative",
  overflow: "hidden",

  "&:hover .play-button": {
    transform: "scale(1.1)",
  },
  "&:hover .hover-ring": {
    transform: "scale(1.25)",
    opacity: 1,
  },
}));

const GridMatrix = styled(Box)({
  position: "absolute",
  inset: 0,
  display: "grid",
  gridTemplateColumns: "repeat(8, 1fr)",
  gridTemplateRows: "repeat(6, 1fr)",
});

const GridCell = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  animation: `${cellPulse} 2s ease-in-out infinite`,
  // Each cell will get a slightly different animation delay based on its index
});

const GridDot = styled(Box)(({ delay = 0 }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: "rgba(52, 211, 153, 0.5)", // emerald-400/30
  animation: `${dotPulse} 6s ease-in-out infinite`,
  animationDelay: `${delay}ms`,
}));

const ParticleEffects = styled(Box)({
  position: "absolute",
  inset: 0,
});

const Particle = styled(Box)(({ top, left, color, delay = 0 }) => ({
  position: "absolute",
  top,
  left,
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  backgroundColor: color,
  filter: "blur(4px)",
  opacity: 0.4,
  animation: `${float} 15s cubic-bezier(0.45, 0, 0.55, 1) infinite`,
  animationDelay: `${delay}ms`,
}));

const DataStreams = styled(Box)({
  position: "absolute",
  inset: 0,
});

const HorizontalStream = styled(Box)(({ top, via, delay = 0 }) => ({
  position: "absolute",
  top,
  left: 0,
  width: "100%",
  height: "1px",
  background: `linear-gradient(to right, transparent, ${via}, transparent)`,
  opacity: 0.7,
  animation: `${pulse} 2s infinite`,
  animationDelay: `${delay}ms`,
}));

const VerticalStream = styled(Box)(({ left, via, delay = 0 }) => ({
  position: "absolute",
  top: 0,
  left,
  height: "100%",
  width: "1px",
  background: `linear-gradient(to bottom, transparent, ${via}, transparent)`,
  opacity: 0.7,
  animation: `${pulse} 2s infinite`,
  animationDelay: `${delay}ms`,
}));

const ContentContainer = styled(Box)({
  position: "relative",
  zIndex: 10,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "1.5rem",
});

const CardHeading = styled(Typography)({
  color: "white", // emerald-400
  //fontFamily: "monospace",
  fontWeight: 600,
  fontSize: "1.6rem",
  marginBottom: "1.5rem",
  textAlign: "center",
  //letterSpacing: "0.05em",
});

const PlayButtonContainer = styled(Box)({
  position: "relative",
});

const PlayButtonWrapper = styled(Button)({
  width: "74px",
  height: "74px",
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#065f46", // emerald-950
  border: "1px solid rgba(16, 185, 129, 0.7)", // emerald-500/70
  padding: 0,
  minWidth: 0,
  transition: "transform 0.3s",
});

const PlayButtonGlow = styled(Box)({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  backgroundColor: "rgba(16, 185, 129, 0.1)", // emerald-500/10
});

const HoverRing = styled(Box)({
  position: "absolute",
  inset: 0,
  borderRadius: "50%",
  border: "1px solid rgba(52, 211, 153, 0.5)", // emerald-400/50
  transform: "scale(0)",
  opacity: 0,
  transition: "all 0.7s",
});

const QuantumNeuralMatrixMUI = ({ role }) => {
  // Generate array for grid cells
  const gridCells = Array.from({ length: 48 });

  return (
    <CardContainer>
      <MatrixCard>
        {/* Grid matrix */}
        <GridMatrix>
          {gridCells.map((_, index) => (
            <GridCell
              key={index}
              sx={{ animationDelay: `${(index * 150) % 2000}ms` }}
            >
              {index % 7 === 0 && <GridDot delay={(index * 230) % 3000} />}
            </GridCell>
          ))}
        </GridMatrix>

        {/* Quantum particle effects */}
        <ParticleEffects>
          <Particle
            top="25%"
            left="25%"
            color="rgba(52, 211, 153, 0.4)"
            delay={0}
          />
          <Particle
            top="50%"
            left="33.333%"
            color="rgba(45, 212, 191, 0.4)"
            delay={2000}
          />
          <Particle
            top="75%"
            left="66.666%"
            color="rgba(74, 222, 128, 0.4)"
            delay={1000}
          />
          <Particle
            top="33.333%"
            left="75%"
            color="rgba(34, 211, 238, 0.4)"
            delay={3000}
          />
        </ParticleEffects>

        {/* Data streams */}
        <DataStreams>
          <HorizontalStream top="25%" via="rgba(16, 185, 129, 0.5)" />{" "}
          {/* emerald-500/50 */}
          <HorizontalStream
            top="66.666%"
            via="rgba(20, 184, 166, 0.5)"
            delay={500}
          />{" "}
          {/* teal-500/50 */}
          <VerticalStream
            left="33.333%"
            via="rgba(34, 197, 94, 0.5)"
            delay={300}
          />{" "}
          {/* green-500/50 */}
          <VerticalStream
            left="66.666%"
            via="rgba(6, 182, 212, 0.5)"
            delay={700}
          />{" "}
          {/* cyan-500/50 */}
        </DataStreams>

        <ContentContainer>
          <CardHeading>Lär dig använda Studera.ai</CardHeading>
          <PlayButtonContainer>
            <PlayButtonWrapper className="play-button" disableRipple>
              <PlayButtonGlow />
              <PlayArrowIcon
                sx={{ color: "rgb(52, 211, 153)", fontSize: 42 }}
              />{" "}
              {/* emerald-400 */}
            </PlayButtonWrapper>
            <HoverRing className="hover-ring" />
          </PlayButtonContainer>
        </ContentContainer>
      </MatrixCard>
    </CardContainer>
  );
};

export default QuantumNeuralMatrixMUI;
